import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import { IssuePeekOverview } from '@/components/IssuePeekOverview';
import { Plus, Trash2, CheckSquare, Square, X, Flag, User, RefreshCw } from 'lucide-react';
import clsx from 'clsx';

export function ListPage() {
  const { projectId, workspaceId } = useParams<{ workspaceId: string; projectId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId!),
    enabled: !!projectId,
  });

  const { data: workItems, isLoading } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId!),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.getMembers(workspaceId!),
    enabled: !!workspaceId,
  });

  const { data: cycles } = useQuery({
    queryKey: ['cycles', projectId],
    queryFn: () => api.getCycles(projectId!),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createWorkItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
      setIsCreating(false);
      setNewItemName('');
      setSelectedState('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteWorkItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.updateWorkItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: ({ ids, data }: { ids: string[]; data: any }) =>
      api.bulkUpdateWorkItems(ids, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
      setSelectedItems(new Set());
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteWorkItems(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
      setSelectedItems(new Set());
    },
  });

  // Selection handlers
  const toggleSelectItem = (id: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === workItems?.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(workItems?.map((wi: any) => wi.bh_workitemid) || []));
    }
  };

  const handleBulkStateChange = (stateId: string) => {
    if (selectedItems.size === 0) return;
    bulkUpdateMutation.mutate({
      ids: Array.from(selectedItems),
      data: { bh_state: stateId },
    });
  };

  const handleBulkPriorityChange = (priority: number) => {
    if (selectedItems.size === 0) return;
    bulkUpdateMutation.mutate({
      ids: Array.from(selectedItems),
      data: { bh_priority: priority },
    });
  };

  const handleBulkAssigneeChange = (assignee: string | null) => {
    if (selectedItems.size === 0) return;
    bulkUpdateMutation.mutate({
      ids: Array.from(selectedItems),
      data: { bh_assignee: assignee },
    });
  };

  const handleBulkCycleChange = (cycleId: string | null) => {
    if (selectedItems.size === 0) return;
    bulkUpdateMutation.mutate({
      ids: Array.from(selectedItems),
      data: { bh_cycle: cycleId },
    });
  };

  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;
    if (confirm(`Delete ${selectedItems.size} selected items?`)) {
      bulkDeleteMutation.mutate(Array.from(selectedItems));
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemName.trim() || !selectedState) return;

    createMutation.mutate({
      bh_name: newItemName.trim(),
      bh_project: projectId,
      bh_state: selectedState,
    });
  };

  const getPriorityLabel = (priority: number | string) => {
    const p = typeof priority === 'number' ? priority : parseInt(priority) || 1;
    switch (p) {
      case 5: return { label: 'Urgent', color: 'bg-red-100 text-red-700' };
      case 4: return { label: 'High', color: 'bg-orange-100 text-orange-700' };
      case 3: return { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' };
      case 2: return { label: 'Low', color: 'bg-green-100 text-green-700' };
      default: return { label: 'None', color: 'bg-gray-100 text-gray-600' };
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">List View</h1>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Item
        </button>
      </div>

      {isCreating && (
        <form onSubmit={handleCreate} className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex gap-4">
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder="What needs to be done?"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select state</option>
              {states?.map((state: any) => (
                <option key={state.bh_stateid} value={state.bh_stateid}>
                  {state.bh_name}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={createMutation.isPending || !newItemName.trim() || !selectedState}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setIsCreating(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Bulk Actions Bar */}
      {selectedItems.size > 0 && (
        <div className="px-4 py-3 bg-primary-50 border-b border-primary-100 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-primary-700">
              {selectedItems.size} selected
            </span>
            <button
              onClick={() => setSelectedItems(new Set())}
              className="p-1 text-primary-600 hover:bg-primary-100 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="h-5 w-px bg-primary-200" />

          {/* State */}
          <div className="flex items-center gap-1">
            <select
              onChange={(e) => handleBulkStateChange(e.target.value)}
              className="text-sm border border-primary-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              defaultValue=""
            >
              <option value="" disabled>Change State</option>
              {states?.map((state: any) => (
                <option key={state.bh_stateid} value={state.bh_stateid}>
                  {state.bh_name}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div className="flex items-center gap-1">
            <Flag className="w-4 h-4 text-primary-600" />
            <select
              onChange={(e) => handleBulkPriorityChange(parseInt(e.target.value))}
              className="text-sm border border-primary-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              defaultValue=""
            >
              <option value="" disabled>Priority</option>
              <option value={1}>None</option>
              <option value={2}>Low</option>
              <option value={3}>Medium</option>
              <option value={4}>High</option>
              <option value={5}>Urgent</option>
            </select>
          </div>

          {/* Assignee */}
          <div className="flex items-center gap-1">
            <User className="w-4 h-4 text-primary-600" />
            <select
              onChange={(e) => handleBulkAssigneeChange(e.target.value || null)}
              className="text-sm border border-primary-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              defaultValue=""
            >
              <option value="" disabled>Assignee</option>
              <option value="">Unassign</option>
              {members?.map((member: any) => (
                <option key={member.bh_memberid} value={member.bh_memberid}>
                  {member.bh_name}
                </option>
              ))}
            </select>
          </div>

          {/* Cycle */}
          <div className="flex items-center gap-1">
            <RefreshCw className="w-4 h-4 text-primary-600" />
            <select
              onChange={(e) => handleBulkCycleChange(e.target.value || null)}
              className="text-sm border border-primary-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              defaultValue=""
            >
              <option value="" disabled>Cycle</option>
              <option value="">No Cycle</option>
              {cycles?.map((cycle: any) => (
                <option key={cycle.bh_cycleid} value={cycle.bh_cycleid}>
                  {cycle.bh_name}
                </option>
              ))}
            </select>
          </div>

          <div className="h-5 w-px bg-primary-200" />

          {/* Delete */}
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="w-12 px-4 py-3">
                <button
                  onClick={toggleSelectAll}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  {selectedItems.size === workItems?.length && workItems?.length > 0 ? (
                    <CheckSquare className="w-4 h-4 text-primary-600" />
                  ) : (
                    <Square className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                State
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {workItems?.map((item: any) => {
              const priority = getPriorityLabel(item.bh_priority);
              const isSelected = selectedItems.has(item.bh_workitemid);
              return (
                <tr
                  key={item.bh_workitemid}
                  className={clsx('hover:bg-gray-50', isSelected && 'bg-primary-50')}
                >
                  <td className="w-12 px-4 py-3">
                    <button
                      onClick={() => toggleSelectItem(item.bh_workitemid)}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      {isSelected ? (
                        <CheckSquare className="w-4 h-4 text-primary-600" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                    #{item.bh_sequenceid}
                  </td>
                  <td
                    className="px-4 py-3 cursor-pointer"
                    onClick={() => setSelectedIssueId(item.bh_workitemid)}
                  >
                    <span className="text-sm font-medium text-gray-900 hover:text-primary-600">
                      {item.bh_name}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={item._bh_state_value}
                      onChange={(e) => {
                        updateMutation.mutate({
                          id: item.bh_workitemid,
                          data: { bh_state: e.target.value },
                        });
                      }}
                      className="text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {states?.map((state: any) => (
                        <option key={state.bh_stateid} value={state.bh_stateid}>
                          {state.bh_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={item.bh_priority || 1}
                      onChange={(e) => {
                        updateMutation.mutate({
                          id: item.bh_workitemid,
                          data: { bh_priority: parseInt(e.target.value) },
                        });
                      }}
                      className={clsx(
                        'text-xs px-2 py-1 rounded-full font-medium border-0 focus:outline-none focus:ring-2 focus:ring-primary-500',
                        priority.color
                      )}
                    >
                      <option value={1}>None</option>
                      <option value={2}>Low</option>
                      <option value={3}>Medium</option>
                      <option value={4}>High</option>
                      <option value={5}>Urgent</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(item.createdon).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        if (confirm('Delete this item?')) {
                          deleteMutation.mutate(item.bh_workitemid);
                        }
                      }}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}

            {workItems?.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                  No work items yet. Create your first one!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Issue Peek Overview */}
      {selectedIssueId && (
        <IssuePeekOverview
          issueId={selectedIssueId}
          projectId={projectId!}
          onClose={() => setSelectedIssueId(null)}
        />
      )}
    </div>
  );
}
